/**
 * FeedbackController — manages post-call feedback submission.
 *
 * Lit Reactive Controller equivalent of useRinggFeedback from React Native.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { EventBus } from "@ringg/core";
import { RinggApiClient, WidgetStateController } from "@ringg/core";

export class FeedbackController implements ReactiveController {
  host: ReactiveControllerHost;

  private api: RinggApiClient;
  private widgetState: WidgetStateController;
  private eventBus: EventBus;

  isSubmitting = false;

  constructor(host: ReactiveControllerHost, api: RinggApiClient, widgetState: WidgetStateController, eventBus: EventBus) {
    this.host = host;
    this.api = api;
    this.widgetState = widgetState;
    this.eventBus = eventBus;
    host.addController(this);
  }

  hostConnected(): void {}
  hostDisconnected(): void {}

  async submit(rating: number, comment: string): Promise<void> {
    const callId = this.widgetState.getSnapshot().currentCallId;
    if (!callId) {
      this.widgetState.close();
      this.host.requestUpdate();
      return;
    }

    this.isSubmitting = true;
    this.host.requestUpdate();

    try {
      await this.api.submitFeedback(callId, { rating, comment });
      this.eventBus.emit("ringg:feedback_status", {
        status: "submitted",
        callId,
        rating,
      });
    } catch (err) {
      console.error("[FeedbackController] Error submitting feedback:", err);
    } finally {
      this.isSubmitting = false;
      this.widgetState.close();
      this.host.requestUpdate();
    }
  }

  skip(): void {
    const callId = this.widgetState.getSnapshot().currentCallId;
    if (callId) {
      this.eventBus.emit("ringg:feedback_status", {
        status: "skipped",
        callId,
      });
    }
    this.widgetState.close();
    this.host.requestUpdate();
  }
}
